/**
 * ============================================================================
 * RequalTaskCard — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: render a single requalification row as a Kanban card. Shows
 *          equipment tag + name, criticality chip, trigger-source chip,
 *          scheduled date, days-until pill, and a state-appropriate CTA
 *          (Start when PENDING; Complete when IN_PROGRESS; nothing when
 *          COMPLETED).
 *
 * A11Y: actions are real <button> with aria-label; the trigger chip exposes
 *  the source via aria-label.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './RequalificationDashboard.module.css';
import type { RequalScheduleRow } from './useRequalificationScheduleQuery';
import type { EquipmentCriticality } from '../EquipmentWorkbench/useEquipmentQuery';
import type { RequalificationTrigger } from '../EquipmentDetail/useEquipmentDetailQuery';

const CRIT_CLASS: Record<EquipmentCriticality, string> = {
  CRITICAL: styles.critCritical,
  MAJOR: styles.critMajor,
  MINOR: styles.critMinor,
  NON_GMP: styles.critNonGmp,
};

const TRIGGER_CLASS: Record<RequalificationTrigger, string> = {
  CALENDAR: styles.triggerCalendar,
  CHANGE_CONTROL: styles.triggerChangeControl,
  DEVIATION: styles.triggerDeviation,
  LOCATION_CHANGE: styles.triggerLocationChange,
};

export interface RequalTaskCardProps {
  row: RequalScheduleRow;
  onStart: (row: RequalScheduleRow) => void;
  onComplete: (row: RequalScheduleRow) => void;
}

export function RequalTaskCard({
  row,
  onStart,
  onComplete,
}: RequalTaskCardProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const d = row.days_until_due;
  let pillClass: string;
  let pillLabel: string;
  if (row.state === 'COMPLETED') {
    pillClass = styles.dueDone;
    pillLabel = t('equipment.requalDashboard.completed', {
      defaultValue: 'Completed',
    });
  } else if (d < 0) {
    pillClass = styles.dueOverdue;
    pillLabel = t('equipment.requalDashboard.overdueDays', {
      defaultValue: '{{n}} days overdue',
      n: nf.format(Math.abs(d)),
    });
  } else if (d <= 14) {
    pillClass = styles.dueSoon;
    pillLabel = t('equipment.requalDashboard.dueIn', {
      defaultValue: 'Due in {{n}} days',
      n: nf.format(d),
    });
  } else {
    pillClass = styles.dueLater;
    pillLabel = t('equipment.requalDashboard.dueIn', {
      defaultValue: 'Due in {{n}} days',
      n: nf.format(d),
    });
  }

  return (
    <article
      className={styles.card}
      aria-label={t('equipment.requalDashboard.cardAria', {
        defaultValue:
          'Requalification for {{tag}} ({{name}}), trigger {{trigger}}',
        tag: row.equipment_tag,
        name: row.equipment_name,
        trigger: t(`equipment.detail.requalifications.trigger.${row.trigger}`, {
          defaultValue: row.trigger,
        }),
      })}
    >
      <div className={styles.cardTitleRow}>
        <code className={styles.tagMono}>{row.equipment_tag}</code>
        <h3 className={styles.cardName}>{row.equipment_name}</h3>
      </div>
      <div className={styles.cardTitleRow}>
        <span className={`${styles.chip} ${CRIT_CLASS[row.criticality]}`}>
          {t(`equipment.criticality.${row.criticality}`, {
            defaultValue: row.criticality,
          })}
        </span>
        <span
          className={`${styles.chip} ${TRIGGER_CLASS[row.trigger]}`}
          aria-label={t('equipment.requalDashboard.triggerAria', {
            defaultValue: 'Trigger: {{trigger}}',
            trigger: t(
              `equipment.detail.requalifications.trigger.${row.trigger}`,
              { defaultValue: row.trigger },
            ),
          })}
        >
          {t(`equipment.detail.requalifications.trigger.${row.trigger}`, {
            defaultValue: row.trigger,
          })}
        </span>
        <span className={`${styles.duePill} ${pillClass}`}>{pillLabel}</span>
      </div>
      <div className={styles.cardMeta}>
        <span>
          {t('equipment.requalDashboard.scheduled', {
            defaultValue: 'Scheduled: {{ts}}',
            ts: dateFmt.format(new Date(row.scheduled_for_utc)),
          })}
        </span>
        {row.validation_lead && (
          <span>
            {t('equipment.requalDashboard.lead', {
              defaultValue: 'Lead: {{name}}',
              name: row.validation_lead,
            })}
          </span>
        )}
      </div>
      {row.state !== 'COMPLETED' && (
        <div className={styles.cardActions}>
          {row.state === 'PENDING' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onStart(row)}
              aria-label={t('equipment.requalDashboard.startAria', {
                defaultValue: 'Start requalification for {{tag}}',
                tag: row.equipment_tag,
              })}
            >
              {t('equipment.requalDashboard.start', { defaultValue: 'Start' })}
            </Button>
          )}
          {row.state === 'IN_PROGRESS' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onComplete(row)}
              aria-label={t('equipment.requalDashboard.completeAria', {
                defaultValue: 'Complete requalification for {{tag}}',
                tag: row.equipment_tag,
              })}
            >
              {t('equipment.requalDashboard.complete', {
                defaultValue: 'Complete',
              })}
            </Button>
          )}
        </div>
      )}
    </article>
  );
}
